// ============================================================
// Designations Page — Staff Management
// ============================================================

import { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Stack,
  IconButton,
  TextField,
  InputAdornment,
  MenuItem,
  Chip,
  Tooltip,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Plus, Search, Edit, Trash2, Tag, Building2, FilterX, Lock } from 'lucide-react';
import {
  useDesignations,
  useDepartments,
  useCreateDesignation,
  useUpdateDesignation,
  useDeleteDesignation,
} from '@/hooks/useStaffManagement';
import { PageHeader } from '@/components/navigation/PageHeader';
import { ConfirmationDialog } from '@/components/dialogs/ConfirmationDialog';
import { DesignationModal } from './components/DesignationModal';
import { ErrorState } from '@/components/feedback/ErrorState';
import { usePageMeta } from '@/hooks/usePageMeta';
import { usePermission } from '@/hooks/usePermission';
import { PERMISSIONS } from '@/constants/permissions';
import toast from 'react-hot-toast';

export function DesignationsPage() {
  usePageMeta('Designations | Staff Management', [
    { label: 'Staff Management', path: '/staff-management' },
    { label: 'Designations' },
  ]);

  const { data: designations = [], isLoading, error, refetch } = useDesignations();
  const { data: departments = [] } = useDepartments();
  const createMutation = useCreateDesignation();
  const updateMutation = useUpdateDesignation();
  const deleteMutation = useDeleteDesignation();

  const { can } = usePermission();
  const canCreate = can(PERMISSIONS.STAFF_MANAGE_CREATE) || can(PERMISSIONS.STAFF_MANAGE_VIEW);
  const canUpdate = can(PERMISSIONS.STAFF_MANAGE_UPDATE) || can(PERMISSIONS.STAFF_MANAGE_VIEW);
  const canDelete = can(PERMISSIONS.STAFF_MANAGE_DELETE) || can(PERMISSIONS.STAFF_MANAGE_VIEW);

  const [search, setSearch] = useState('');
  const [selectedDept, setSelectedDept] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingDesig, setEditingDesig] = useState(null);
  const [deleteDesig, setDeleteDesig] = useState(null);

  const deptMap = (departments || []).reduce((acc, dept) => {
    acc[dept.id] = dept.name;
    return acc;
  }, {});

  const filteredDesignations = (designations || []).filter((item) => {
    const matchSearch =
      !search.trim() ||
      item.name?.toLowerCase().includes(search.toLowerCase()) ||
      item.description?.toLowerCase().includes(search.toLowerCase());
    const matchDept =
      !selectedDept ||
      item.department_id === selectedDept ||
      item.departmentId === selectedDept ||
      item.department?.id === selectedDept;
    return matchSearch && matchDept;
  });

  const isProtectedDesignation = (desig) => {
    if (!desig) return false;
    const deptId = desig.department_id || desig.departmentId || desig.department?.id;
    const deptName = desig.department?.name || deptMap[deptId] || '';
    const isDeptAdmin = String(deptName).trim().toUpperCase() === 'ADMIN';
    const isPrincipal = String(desig.name || '').trim().toUpperCase() === 'PRINCIPAL';
    return isDeptAdmin || isPrincipal;
  };

  const handleOpenCreate = () => {
    setEditingDesig(null);
    setModalOpen(true);
  };

  const handleOpenEdit = (desig) => {
    if (isProtectedDesignation(desig)) {
      toast.error('System ADMIN designation cannot be modified');
      return;
    }
    setEditingDesig(desig);
    setModalOpen(true);
  };

  const handleSubmitModal = async (payload) => {
    try {
      if (editingDesig) {
        await updateMutation.mutateAsync({ id: editingDesig.id, ...payload });
        toast.success('Designation updated successfully');
      } else {
        await createMutation.mutateAsync(payload);
        toast.success('Designation created successfully');
      }
      setModalOpen(false);
      setEditingDesig(null);
    } catch (err) {
      toast.error(err.message || 'Failed to save designation');
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteDesig) return;
    if (isProtectedDesignation(deleteDesig)) {
      toast.error('System ADMIN designation cannot be deleted');
      setDeleteDesig(null);
      return;
    }
    try {
      await deleteMutation.mutateAsync(deleteDesig.id);
      toast.success('Designation deleted successfully');
      setDeleteDesig(null);
    } catch (err) {
      toast.error(err.message || 'Failed to delete designation');
    }
  };

  const columns = [
    {
      field: 'name',
      headerName: 'Designation Name',
      width: 260,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, height: '100%' }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: 1.5,
              bgcolor: 'secondary.main',
              color: 'secondary.contrastText',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Tag size={16} />
          </Box>
          <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', lineHeight: 1.5 }}>
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'department_id',
      headerName: 'Department',
      width: 240,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params) => {
        const deptId = params.row.department_id || params.row.departmentId || params.row.department?.id;
        const deptName = params.row.department?.name || deptMap[deptId] || 'Unassigned';
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Chip
              icon={<Building2 size={14} />}
              label={deptName}
              size="small"
              variant="outlined"
              sx={{ fontWeight: 500, borderColor: 'divider', bgcolor: 'background.paper' }}
            />
          </Box>
        );
      },
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 1,
      minWidth: 320,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.5 }}>
            {params.value || 'No description provided.'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      align: 'right',
      headerAlign: 'right',
      sortable: false,
      renderCell: (params) => {
        const isProtected = isProtectedDesignation(params.row);
        if (isProtected) {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', width: '100%', height: '100%', pr: 1 }}>
              <Tooltip title="System Designation (Protected)">
                <Chip
                  icon={<Lock size={12} />}
                  label="Protected"
                  size="small"
                  variant="outlined"
                  sx={{ height: 22, fontSize: '0.7rem', fontWeight: 600, color: 'text.secondary', borderColor: 'divider' }}
                />
              </Tooltip>
            </Box>
          );
        }
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', width: '100%', height: '100%', gap: 0.5 }}>
            {canUpdate && (
              <Tooltip title="Edit Designation">
                <IconButton
                  size="small"
                  onClick={() => handleOpenEdit(params.row)}
                  sx={{ color: 'primary.main' }}
                >
                  <Edit size={16} />
                </IconButton>
              </Tooltip>
            )}
            {canDelete && (
              <Tooltip title="Delete Designation">
                <IconButton
                  size="small"
                  onClick={() => setDeleteDesig(params.row)}
                  sx={{ color: 'error.main' }}
                >
                  <Trash2 size={16} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        );
      },
    },
  ];

  if (error) {
    return (
      <Box>
        <ErrorState error={error} onRetry={refetch} />
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Designations"
        subtitle="Define role designations associated with specific departments"
        actions={
          canCreate && (
            <Button
              variant="contained"
              startIcon={<Plus size={18} />}
              onClick={handleOpenCreate}
              sx={{ fontWeight: 600 }}
            >
              Add Designation
            </Button>
          )
        }
      />

      {/* Filters and Search Bar matching TeacherListPage */}
      <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 2, mb: 3 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Search designations..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            fullWidth
            sx={{ flex: 1, minWidth: { xs: '100%', sm: 300 } }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={18} />
                  </InputAdornment>
                ),
              },
            }}
          />

          <TextField
            select
            size="small"
            label="Department"
            value={selectedDept}
            onChange={(e) => setSelectedDept(e.target.value)}
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="">All Departments</MenuItem>
            {(departments || []).map((dept) => (
              <MenuItem key={dept.id} value={dept.id}>
                {dept.name}
              </MenuItem>
            ))}
          </TextField>

          {(search || selectedDept) && (
            <Button
              variant="outlined"
              startIcon={<FilterX size={18} />}
              onClick={() => {
                setSearch('');
                setSelectedDept('');
              }}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Clear Filters
            </Button>
          )}
        </Stack>
      </Box>

      <Box sx={{ width: '100%', bgcolor: 'background.paper', borderRadius: 2, overflow: 'hidden' }}>
        <DataGrid
          autoHeight
          rows={filteredDesignations}
          columns={columns}
          loading={isLoading}
          getRowId={(row) => row.id || Math.random().toString()}
          pageSizeOptions={[10, 25, 50]}
          initialState={{
            pagination: { paginationModel: { pageSize: 10 } },
          }}
          disableRowSelectionOnClick
          sx={{
            border: 0,
            '& .MuiDataGrid-cell': {
              display: 'flex',
              alignItems: 'center',
            },
          }}
        />
      </Box>

      {/* Create / Edit Modal */}
      <DesignationModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        designation={editingDesig}
        departments={departments}
        onSubmit={handleSubmitModal}
        loading={createMutation.isPending || updateMutation.isPending}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={!!deleteDesig}
        onClose={() => setDeleteDesig(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete Designation"
        message={`Are you sure you want to delete the designation "${deleteDesig?.name}"?`}
        confirmText="Delete"
        confirmColor="error"
        loading={deleteMutation.isPending}
      />
    </Box>
  );
}
